import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import { useCardsSearchInfiniteQuery } from 'store/services/cards/search/cardsSearchApi';
import { pickAltNames } from 'utils/cardNames';
import HoverOverlay from 'screens/Main/Search/Components/HoverOverlay';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { decimalToPoints } from 'utils/decimalUtils';
import { useWindowSize } from 'hooks/useWindowSize';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import './SearchFromAnywhere.css';

// Routes where SearchFromAnywhere should be disabled
const EXCLUDED_ROUTES = [
  '/',
  '/welcome',
  '/onboarding',
  '/reset-password',
  '/verify-email',
  '/verify-email-change',
  // TradeMagic Play: a player typing a result must never summon the global search.
  '/play',
];

const SearchFromAnywhere: React.FC = () => {
  const { isMobile } = useWindowSize(768);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigationCooldownRef = useRef(false);

  // After a route change, disable keyboard handler briefly to let the page settle
  useEffect(() => {
    navigationCooldownRef.current = true;
    const timer = setTimeout(() => {
      navigationCooldownRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Check if current route is excluded
  const isExcludedRoute = EXCLUDED_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Debounce search query to avoid excessive API calls
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Build search params from debounced query
  const searchParams = useMemo(() => {
    if (!debouncedQuery.trim()) return {};
    return { name: debouncedQuery.trim() };
  }, [debouncedQuery]);

  // Fetch search results
  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useCardsSearchInfiniteQuery(searchParams, {
    skip: Object.keys(searchParams).length === 0,
  });

  // Flatten all pages into a single array of cards
  const allCards = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap(page => page.cards);
  }, [infiniteData]);

  // Close the overlay and reset state
  const closeOverlay = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHoveredCard(null);
  }, []);

  // Handle backdrop click - close overlay
  const handleBackdropClick = useCallback(() => {
    closeOverlay();
  }, [closeOverlay]);

  // Infinite scroll callback
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading || isFetchingNextPage || !hasNextPage || !node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px',
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Global keyboard handler - only handles opening the overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if on mobile or excluded route
      if (isMobile || isExcludedRoute) return;

      // Skip if overlay is already open (input handles its own events)
      if (isOpen) {
        // Only handle Escape to close
        if (e.key === 'Escape') {
          e.preventDefault();
          closeOverlay();
        }
        return;
      }

      // Skip if user is in an input field
      const activeElement = document.activeElement;
      const isInputActive =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'SELECT' ||
        activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputActive) return;

      // Skip during navigation cooldown — let destination page settle
      if (navigationCooldownRef.current) return;

      // Open overlay on printable character (ignore modifier combos)
      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setIsOpen(true);
        setSearchQuery(e.key);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMobile, isExcludedRoute, closeOverlay]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle input keydown (for Escape)
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeOverlay();
    }
  };

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle card hover
  const handleCardInteraction = (cardId: string) => {
    setHoveredCard(cardId);
  };

  const handleCardLeave = () => {
    setHoveredCard(null);
  };

  // Don't render on mobile, excluded routes, or when closed
  if (isMobile || isExcludedRoute || !isOpen) return null;

  return createPortal(
    <div className="search-anywhere-backdrop" onClick={handleBackdropClick}>
      <div className="search-anywhere-container">
        {/* Search input */}
        <div className="search-anywhere-input-area" onClick={(e) => e.stopPropagation()}>
          <input
            ref={inputRef}
            type="text"
            className="search-anywhere-input"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="Search for cards..."
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {/* Results area */}
        <div className="search-anywhere-results-container">
          {/* Loading state */}
          {isLoading && searchQuery.trim() && (
            <div className="search-anywhere-loading">
              <LoadingIndicator />
              <span>Searching...</span>
            </div>
          )}

          {/* Empty state - no query */}
          {!searchQuery.trim() && (
            <div className="search-anywhere-hint">
              Start typing to search for cards...
            </div>
          )}

          {/* No results */}
          {!isLoading && searchQuery.trim() && allCards.length === 0 && debouncedQuery === searchQuery && (
            <div className="search-anywhere-no-results">
              No cards found for "{searchQuery}"
            </div>
          )}

          {/* Results grid */}
          {allCards.length > 0 && (
            <>
              <div className="search-anywhere-results-count">
                Found {allCards.length} card{allCards.length !== 1 ? 's' : ''}
                {hasNextPage && ' (scroll for more)'}
              </div>
              <div className="search-anywhere-results">
                {allCards.map((card, index) => {
                  const scryfallId = (card as any).scryfallId || card.scryfallID;

                  return (
                    <div
                      key={`${card.id}-${index}`}
                      className="search-anywhere-card-wrapper"
                      onMouseEnter={() => handleCardInteraction(scryfallId)}
                      onMouseLeave={handleCardLeave}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {hoveredCard === scryfallId ? (
                        <HoverOverlay
                          title={card.name}
                          altName={pickAltNames({
                            name: card.name,
                            printedName: (card as any).printedName,
                            flavorName: (card as any).flavorName,
                            lang: (card as any).lang,
                          }).primary ?? undefined}
                          cardId={scryfallId}
                          oracleId={card.oracleId || undefined}
                          setName={card.setName}
                          setCode={(card as any).setCode}
                          setIconUrl={(card as any).setIconUrl}
                          collectorNumber={(card as any).collectorNumber}
                          availableInNonFoil={(card as any).availableInNonFoil}
                          availableInFoil={(card as any).availableInFoil}
                          onNavigate={closeOverlay}
                          nonFoilPrice={(() => {
                            if ((card as any).nonFoilPoints !== undefined && (card as any).nonFoilPoints !== null) {
                              return (card as any).nonFoilPoints;
                            }
                            if ((card as any).pointValue !== undefined && (card as any).pointValue !== null) {
                              return (card as any).pointValue;
                            }
                            if (Array.isArray(card.price) && card.price.length > 0) {
                              const priceData = card.price[0];
                              if (priceData?.usd) {
                                const points = decimalToPoints(priceData.usd);
                                return points !== null ? points : 0;
                              }
                            }
                            return 0;
                          })()}
                          foilPrice={(() => {
                            if ((card as any).foilPoints !== undefined && (card as any).foilPoints !== null) {
                              return (card as any).foilPoints;
                            }
                            if (Array.isArray(card.price) && card.price.length > 0) {
                              const priceData = card.price[0];
                              if (priceData?.usdFoil) {
                                const points = decimalToPoints(priceData.usdFoil);
                                return points !== null ? points : 0;
                              }
                            }
                            return 0;
                          })()}
                        />
                      ) : (
                        <img
                          src={(() => {
                            const cardFaces = (card as any)?.card_faces || (card as any)?.cardFaces;
                            const possibleImages = [
                              card.imageUris?.normal,
                              card.imageUris?.large,
                              card.imageUris?.small,
                              (card as any)?.image_uris?.normal,
                              (card as any)?.image_uris?.large,
                              (card as any)?.image_uris?.small,
                              cardFaces?.[0]?.image_uris?.normal,
                              cardFaces?.[0]?.image_uris?.large,
                              cardFaces?.[0]?.imageUris?.normal,
                              cardFaces?.[0]?.imageUris?.large,
                            ];
                            const validImage = possibleImages.find(img => img && img !== '');
                            return validImage || 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                          })()}
                          alt={card.name}
                          className="search-anywhere-card-image"
                          onError={(e) => {
                            const img = e.currentTarget;
                            const fallbacks = (card as any)?.normal_fallbacks;
                            if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                              img.dataset.fallbackAttempted = 'true';
                              img.src = fallbacks[0];
                              return;
                            }
                            img.src = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Infinite scroll trigger */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="search-anywhere-load-more">
                  {isFetchingNextPage && (
                    <span className="search-anywhere-loading-more">Loading more cards...</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchFromAnywhere;
